import React from "react";

import { FragmentProvider } from "core/components/FragmentProvider";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { IQueryRoot, QueryRoot } from "core/decorators/QueryRoot";
import initialState from "core/reducers/initialState";
import StringUtil from "core/util/StringUtil";
import { addEnum, Field, Operation } from "typegraph";
import { FragmentLayout } from "../../core/components/FragmentLayout";
import labelProvider from './Catalog.labels';
import CatalogLabels from "./Catalog.labels";
import * as catalogActions from "./catalogActions";
import CourseListPanelConfig from "./config/CourseListPanelConfig";
import Badge from "./entities/Badge";
import Course from "./entities/Course";
import { courseParamNames, ICourseParams } from "./entities/CourseParams";
import * as badgeFragments from "./fragments/badge/displayFragments";
import * as itemContainerFragments from "./fragments/container/itemContainerFragments";
import * as parentContainerFragments from "./fragments/container/parentContainerFragments";
import * as actionFragments from "./fragments/course/actionFragments";
import * as compositeFragments from "./fragments/course/compositeFragments";
import * as displayFragments from "./fragments/course/displayFragments";
import ICourseFragmentProps from "./fragments/course/ICourseFragmentProps";
import * as filterFragments from "./fragments/filterFragments";
import * as miscFragments from "./fragments/generalFragments";
import { ICatalogMetaProps } from "./fragments/ICatalogMetaProps";
import PagingInfo from "common/entities/PagingInfo";


function mapCourseParams(params: any = {}) {
	return { params };
}

class FilteredCoursesResult {
	@Field({ entity: Course })
	public courses: null | Course[];

	@Field({ entity: PagingInfo })
	public paging: null | PagingInfo;
}

@Operation({ type: "query", name: "getCourseList" })
class CourseQuery {
	@Field({ entity: FilteredCoursesResult, aliasFor: "filtered_courses", params: mapCourseParams })
	public CourseResults: null | FilteredCoursesResult;
}

// panel level fragments for intro and filtering
const generalFragments = {
	...filterFragments,
	...miscFragments
};

// what type of list
const ContainerFragments = FragmentProvider.forFragments("catalog-container", parentContainerFragments);

// what type of course wrapper
const ItemFragments = FragmentProvider.forFragments("catalog-item", itemContainerFragments);

// the info available for a course
const courseFragments = {
	...actionFragments,
	...displayFragments,
	...compositeFragments,
};

const now = (new Date()).toISOString();

function mergeConfigAndParams(config: CourseListPanelConfig, pageParams: { [key: string]: string; }, user: typeof initialState.user) {

	const p: ICourseParams = {};

	p.language = user.language || 'en';

	for (const [key, value] of Object.entries({...config.preselectedParams || {}, ...pageParams})) {

		if(!courseParamNames.includes(key)) {
			continue;
		}

		if (key === 'tagIds') {
			if (value) {
				p[key] = StringUtil.toIntegerList(value);
			}
			continue;
		}

		if (typeof value === 'string' && value.startsWith('ENUM_')) {
			p[key] = addEnum(value.slice('ENUM_'.length));
			continue;
		}

		if (key === 'pageSize') {
			p[key] = StringUtil.toInteger(value, 10);
			continue;
		}

		if (key === 'pageNumber') {
			p[key] = StringUtil.toInteger(value, 0);
			continue;
		}

		if (value === '_NOW') {
			p[key] = now;
			continue;
		}

		if (key === 'tagOrCondition') {
			p[key] = pageParams.tagCondition === 'OR';
            continue;
    	}
		if (key === 'isLoadMore') {
			p[key]=value
			continue;
		}
		p[key] = value;
	}

	return p;
}

@Panel({ id: 'course-list', group: "catalog", configClass: CourseListPanelConfig, labelProvider, pageParams: true})
@QueryRoot(CourseQuery, mergeConfigAndParams)
class CourseListPanel extends React.Component<IPanelProps<CourseListPanelConfig> & IQueryRoot<CourseQuery, ICourseParams>, {container: string}> {
	public state = {container: null};

	private setParams = (newParams: ICourseParams) => {
		const { actions } = this.props;

		actions.pushPageParams(...Object.entries(newParams).map(([key, value]) => ({key, value})));
	}

	private replaceParams = (newParams: ICourseParams) => {
		const { actions } = this.props;

		actions.replacePageParams(...Object.entries(newParams).map(([key, value]) => ({key, value})));
	}

	componentWillMount(){
		this.props.actions.removePageParams(["entryId", "itemId","subscribed"]);
	}

	componentWillUnmount(){
		this.props.actions.removePageParams(["sort", "tagIds","pageNumber","query"]);
	}

	private renderItem = (item: Course, index: number) => {
		const { config, queryParams, actions, cartItems, quantityInCart } = this.props;

		const courseProps: ICourseFragmentProps = {
			course: item,
			params: queryParams,
			actions: catalogActions,
			pageActions: actions,
			cartItems: cartItems,
			renderBadge: this.renderBadge,
			showNew: config.newBanner,
			disableDirectBuy: config.disableDirectBuy,
			loginBeforeBuy: config.loginBeforeBuy,
            buySSORedirectPage: config.buySSORedirectPage,
			showCustomFields : config.showCustomFields,
			detailsLinkVisible: true,
			quantityInCart: quantityInCart,
		};

		const itemKey = item && item.id || "course-" + index;

		return (
			<ItemFragments fragments={config.itemFragment} fragmentProps={{key: itemKey}}>
				<FragmentLayout layout={config.courseFragmentLayout} fragments={courseFragments} props={courseProps} />
			</ItemFragments>
		);
	}

	public render() {
		const { config, queryParams, queryData, queryState } = this.props;

		const courses = (queryData && queryData.CourseResults && queryData.CourseResults.courses) || [];
		const paging = (queryData && queryData.CourseResults && queryData.CourseResults.paging) || null;

		let resultsElement;

		// TODO make this a layout
		if(!queryState.loading && (courses == null || courses.length === 0)) {
			resultsElement = <CatalogLabels.Labels.NoResults />;
		} else {
			const courseItemElements = Array.isArray(courses) && courses.map(this.renderItem);

			resultsElement = (
				<ContainerFragments
					fragments={this.state.container || config.containerFragment}
					fragmentProps={{
						courses,
						renderItem: this.renderItem,
						params: queryParams,
						courseFragments: Object.values(courseFragments) as any,
						children: courseItemElements,
						actions: catalogActions
					}}
				/>
			);
		}

		const generalProps: ICatalogMetaProps = {
			loading: queryState.loading,
			params: this.props.pageParams,
			setParams: this.setParams,
			replaceParams: this.replaceParams,
			queryMeta: {
				count: courses ? courses.length : 0,
				pageNumber: queryParams.pageNumber || 0,
				pageSize: queryParams.pageSize || 21,
				total: paging ? paging.totalCount : 0,
				initialPageSize: this.props.config.preselectedParams.pageSize,
			},
			viewOptions: {
				container: this.state.container || config.containerFragment,
				setContainer: (container) => this.setState({container})
			},
			resultsElement
		};

		return (
			<FragmentLayout layout={config.mainLayout} fragments={{...generalFragments}} props={{...generalProps}} />
		);
	}

	private renderBadge = (badge: Badge) => {
		const { config } = this.props;
		return (
			<FragmentLayout fragments={badgeFragments} layout={config.badgeLayout} props={{badge}} />
		);
	}

}


// WEBPACK FOOTER //
// ./src/panels/catalog/CourseList.panel.tsx